import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../apirest';
import generateZPL from '../../utils/codebars/CodeBarsZPL.js';
import './ZplModal.css';

const EXCLUDED_KEYS = ['_id', 'TOTAL'];

const ZplModal = ({ variant, groupStock, onClose }) => {
  const [quantities, setQuantities] = useState({});
  const [barCodes, setBarCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);

  const sizes = useMemo(() => {
    if (!groupStock) return [];
    return Object.keys(groupStock).filter((k) => !EXCLUDED_KEYS.includes(k));
  }, [groupStock]);

  useEffect(() => {
    const initial = {};
    sizes.forEach((size) => { initial[size] = groupStock[size] ?? 0; });
    setQuantities(initial);
  }, [sizes, groupStock]);

  useEffect(() => {
    const fetchCodes = async () => {
      if (!variant?._id) return;
      try {
        const res = await api.get('/stocks/generateBarCodes', { variantId: variant._id });
        setBarCodes(res?.data ?? []);
      } catch {
        toast.error('Erro ao carregar códigos de barras.');
      } finally {
        setLoadingCodes(false);
      }
    };
    fetchCodes();
  }, [variant]);

  const total = useMemo(
    () => Object.values(quantities).reduce((s, v) => s + (Number(v) || 0), 0),
    [quantities]
  );

  const handleChange = (size, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0);
    setQuantities((prev) => ({ ...prev, [size]: num }));
  };

  const handleZero = () => {
    const zeroed = {};
    sizes.forEach((s) => { zeroed[s] = 0; });
    setQuantities(zeroed);
  };

  const handleGenerate = () => {
    if (total === 0) {
      toast.warning('Informe pelo menos uma etiqueta para gerar.');
      return;
    }
    let zplContent = '';
    for (const bc of barCodes) {
      const qty = quantities[bc.size] ?? 0;
      if (qty > 0) {
        zplContent += generateZPL(bc.code128, qty);
      }
    }
    if (!zplContent) {
      toast.warning('Nenhum código de barras encontrado para os tamanhos selecionados.');
      return;
    }
    const blob = new Blob([zplContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'etiquetas.zpl';
    a.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  const product = variant?.product;
  const imgUrl = variant?.pictures?.[0]?.url;

  return (
    <div className="zpl-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="zpl-modal">
        <button className="zpl-close" onClick={onClose} aria-label="Fechar">×</button>

        <h3 className="zpl-title">Gerar etiquetas (ZPL)</h3>

        <div className="zpl-product-card">
          {imgUrl && <img src={imgUrl} alt={product?.name} className="zpl-product-img" />}
          <div className="zpl-product-info">
            {product?.name && <p className="zpl-product-name">{product.name}</p>}
            {product?.code && <p className="zpl-product-meta">Ref: {product.code}</p>}
            {variant?.colorName && <p className="zpl-product-meta">Cor: {variant.colorName}</p>}
          </div>
        </div>

        <p className="zpl-label">Informe a quantidade de etiquetas por tamanho:</p>

        {loadingCodes ? (
          <p className="zpl-loading-text">Carregando códigos de barras...</p>
        ) : (
          <div className="zpl-sizes-grid">
            {sizes.map((size) => (
              <div key={size} className="zpl-size-col">
                <span className="zpl-size-label">{size}</span>
                <input
                  type="number"
                  min="0"
                  className="zpl-size-input"
                  value={quantities[size] ?? 0}
                  onChange={(e) => handleChange(size, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="zpl-total">
          <span className="zpl-total-label">Total de etiquetas</span>
          <span className="zpl-total-value">{total}</span>
        </div>

        <div className="zpl-actions">
          <button className="zpl-btn-zero" onClick={handleZero}>Zerar</button>
          <div className="zpl-actions-right">
            <button className="zpl-btn-cancel" onClick={onClose}>Cancelar</button>
            <button className="zpl-btn-generate" onClick={handleGenerate}>
              Gerar arquivo ZPL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZplModal;
